import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import { Notification } from '@/shared/ui';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { SettingMenuButton } from '@/shared/ui/SettingMenuButton';
import { getRoutePath } from '@/shared/utils/routes';
import type { ICompanyDto } from '@/shared/api/companies';

import { useCompaniesList } from '../model/useCompaniesList';
import { useCompanyName } from '../model/useCompanyName';
import { useDeleteCompany } from '../model/useDeleteCompany';

import { CreateCompanyButton } from './CreateCompanyButton';

function getCompanyInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function getCompanySettingsPath(companyId: string): string {
  return getRoutePath('MAIN', 'COMPANY_SETTINGS')
    .replace(':id', companyId)
    .replace(':tab', 'settings');
}

function getCompanySupportId(company: ICompanyDto): string {
  return company.support_id ?? company.supportId ?? '-';
}

export const CompaniesList = () => {
  const { t } = useTranslation();
  const { companies, isPending, isError, error } = useCompaniesList();
  const navigate = useNavigate();
  const { deleteCompany } = useDeleteCompany();
  const { getCompanyName } = useCompanyName();

  const renderCompanyNotification = () => {
    return isError ? (
      <Notification open={isError} title={error?.message}>
        {error?.data?.details}
      </Notification>
    ) : null;
  };

  if (!companies && isPending) {
    return <Loader containerSize="medium" />;
  }

  if (!companies) {
    return (
      <>
        <NotFoundData
          title={t('company:companiesListNotFound')}
          caption={t('company:companiesListNotFoundCaption')}
          actionSlot={<CreateCompanyButton />}
        />
        {renderCompanyNotification()}
      </>
    );
  }

  const renderSettingsMenu = (companyId: string) => {
    return (
      <SettingMenuButton
        menuItems={[
          {
            label: t('company:list.menu.settings'),
            onClick: () => {
              navigate(getCompanySettingsPath(companyId));
            },
          },
          {
            label: t('company:list.menu.delete'),
            onClick: async () => {
              await deleteCompany(companyId);
            },
            color: 'error',
          },
        ]}
      />
    );
  };

  return (
    <>
      <Box
        sx={{
          mt: 2,
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
          borderRadius: 4,
          bgcolor: 'common.white',
          boxShadow: '0 18px 44px rgba(28, 29, 34, 0.05)',
          overflow: 'hidden',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{
            p: { xs: 2, md: 2.5 },
            background:
              'linear-gradient(135deg, rgba(0,120,212,0.08), rgba(22,132,91,0.06))',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 42,
                height: 42,
                borderRadius: 2.5,
                color: 'primary.main',
                bgcolor: 'common.white',
                boxShadow: '0 10px 24px rgba(0, 120, 212, 0.14)',
              }}
            >
              <ApartmentOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight={700}
              >
                {t('company:list.table.name')}
              </Typography>
              <Typography fontWeight={800}>
                {t('company:list.count', { count: companies.length })}
              </Typography>
            </Box>
          </Stack>
          <CreateCompanyButton
            sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
          />
        </Stack>

        <Stack spacing={1.25} sx={{ p: { xs: 1.5, md: 2 } }}>
          {companies.map((company, index) => {
            const name = getCompanyName(company);
            const companyPath = getCompanySettingsPath(company.id);

            return (
              <Box
                key={company.id}
                sx={{
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'minmax(260px, 1fr) minmax(220px, 0.7fr) auto auto',
                  },
                  gap: { xs: 1.5, md: 2 },
                  alignItems: 'center',
                  p: { xs: 1.5, md: 1.75 },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  bgcolor:
                    index % 2 === 0 ? 'rgba(255,255,255,0.9)' : 'grey.50',
                  transition:
                    'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 14,
                    bottom: 14,
                    left: 0,
                    width: 4,
                    borderRadius: '0 999px 999px 0',
                    bgcolor: 'primary.main',
                    opacity: 0.55,
                  },
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    borderColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.28),
                    boxShadow: '0 14px 30px rgba(28, 29, 34, 0.07)',
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  minWidth={0}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: 2.5,
                      color: 'primary.main',
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.1),
                      border: '1px solid',
                      borderColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.18),
                      fontWeight: 900,
                    }}
                  >
                    {getCompanyInitials(name) || 'CO'}
                  </Box>
                  <Box minWidth={0}>
                    <Typography fontWeight={800} noWrap>
                      {name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {t('company:list.companyId')}: {getCompanySupportId(company)}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={t('company:list.badges.settings')}
                    sx={{
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.1),
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    size="small"
                    label={t('company:list.badges.routing')}
                    sx={{
                      bgcolor: (theme) => alpha(theme.palette.actions.go, 0.12),
                      fontWeight: 700,
                    }}
                  />
                </Stack>

                <Button
                  variant="outlined"
                  endIcon={<ArrowForwardRoundedIcon />}
                  onClick={() => navigate(companyPath)}
                  sx={{
                    justifySelf: { xs: 'stretch', md: 'end' },
                    borderRadius: 2.5,
                    textTransform: 'none',
                    fontWeight: 800,
                    px: 2,
                  }}
                >
                  {t('company:list.open')}
                </Button>

                <Box sx={{ justifySelf: { xs: 'end', md: 'center' } }}>
                  {renderSettingsMenu(company.id)}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {renderCompanyNotification()}
    </>
  );
};
